import React, { useState } from 'react';
import { Box, Container, Typography, Paper } from '@mui/material';
import ParallaxHeader from '../components/ParallaxHeader';
import MapComponent from '../components/Map';
import PlaceIcon from '@mui/icons-material/Place';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import AccessTimeIcon from '@mui/icons-material/AccessTime';

const Contact = () => {
  // Email form state
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setResult(null);
    try {
      const res = await fetch('/api/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          to: process.env.SMTP_TO || 'tabithaanton@stylistinside.com',
          subject: `Contact Form: ${form.name || 'No Name'}`,
          text: `Name: ${form.name}\nEmail: ${form.email}\nPhone: ${form.phone}\nMessage: ${form.message}`,
        }),
      });
      if (res.ok) {
        setResult({ success: true, message: 'Message sent successfully!' });
        setForm({ name: '', email: '', phone: '', message: '' });
      } else {
        const data = await res.json();
        setResult({ success: false, message: data.error || 'Failed to send message.' });
      }
    } catch (err) {
      setResult({ success: false, message: 'Failed to send message.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <ParallaxHeader
        title="Contact Us"
        description="We'd love to hear from you!"
        imageUrl="/images/content/contact-photo-1493775379751-a6c3940f3cbc-2743h.jpg"
        height={300}
      />
      {/* Where to Find Us Section */}
      <Box sx={{ py: 8, backgroundColor: 'background.default' }}>
        <Container maxWidth="md">
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, textAlign: 'center' }}>
            WHERE TO FIND US
          </Typography>
          <Box sx={{ width: '100%', height: { xs: 250, md: 350 }, mb: 2 }}>
            <MapComponent
              lat={28.26441}
              lng={-82.49224}
              zoom={16}
              address="19545 Causeway Blvd, Land O Lakes, FL, 34638, United States"
            />
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              mb: 1,
            }}
          >
            <Box
              component="a"
              href="https://www.google.com/maps/search/?api=1&query=19545+Causeway+Blvd,+Land+O+Lakes,+FL+34638"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                border: '2px solid',
                borderColor: 'primary.main',
                borderRadius: 2,
                px: 3,
                py: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                color: 'primary.main',
                backgroundColor: 'white',
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: { xs: '1rem', md: '1.15rem' },
                boxShadow: 1,
                transition: 'background 0.2s, color 0.2s',
                '&:hover': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                  textDecoration: 'none',
                },
                cursor: 'pointer',
                mb: 1,
                textAlign: 'left',
                flexDirection: 'row',
                width: { xs: '100%', md: 'auto' },
                maxWidth: 500,
              }}
            >
              <PlaceIcon sx={{ mr: 1 }} />
              <span>
                Located in Land O'Lakes, Florida.<br />
                1 Mile West of US Highway 41, Across from Arbor Square (Publix)
              </span>
            </Box>
          </Box>
        </Container>
      </Box>
      {/* Get in Touch Section */}
      <Box sx={{ py: 8, backgroundColor: 'background.paper' }}>
        <Container maxWidth="md">
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, textAlign: 'center' }}>
            GET IN TOUCH
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, alignItems: 'flex-start', justifyContent: 'center' }}>
            {/* Contact Info */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PhoneIcon color="primary" sx={{ mr: 1 }} />
                <a href="tel:8135916538" style={{ color: '#1976d2', textDecoration: 'underline', fontWeight: 600 }}>(813) 591-6538</a>
                <span style={{ margin: '0 8px' }}>or</span>
                <a href="tel:8135264691" style={{ color: '#1976d2', textDecoration: 'underline', fontWeight: 600 }}>(813) 526-4691</a>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <EmailIcon color="primary" sx={{ mr: 1 }} />
                <a href="mailto:tabithaanton@stylistinside.com" style={{ color: '#1976d2', textDecoration: 'underline', fontWeight: 600 }}>tabithaanton@stylistinside.com</a>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                <AccessTimeIcon color="primary" sx={{ mr: 1, mt: 0.5 }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Stylist Inside Schedule:</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    <strong>Sunday:</strong> Closed<br />
                    <strong>Monday - Friday:</strong> 9am to 6pm<br />
                    <strong>Saturday:</strong> 9am to 3pm
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                    Holidays Closed: Thanksgiving, Christmas and New Years
                  </Typography>
                </Box>
              </Box>
            </Box>
            {/* Email Form */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box component="form" onSubmit={handleSubmit} sx={{ mt: { xs: 3, md: 0 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <input
                  type="text"
                  name="name"
                  placeholder="Name"
                  required
                  value={form.name}
                  onChange={handleChange}
                  style={{ padding: '12px', borderRadius: 6, border: '1px solid #ccc', fontSize: '1rem' }}
                />
                <input
                  type="email"
                  name="email"
                  placeholder="Email"
                  required
                  value={form.email}
                  onChange={handleChange}
                  style={{ padding: '12px', borderRadius: 6, border: '1px solid #ccc', fontSize: '1rem' }}
                />
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone"
                  value={form.phone}
                  onChange={handleChange}
                  style={{ padding: '12px', borderRadius: 6, border: '1px solid #ccc', fontSize: '1rem' }}
                />
                <textarea
                  name="message"
                  placeholder="Message"
                  rows={5}
                  required
                  value={form.message}
                  onChange={handleChange}
                  style={{ padding: '12px', borderRadius: 6, border: '1px solid #ccc', fontSize: '1rem', resize: 'vertical' }}
                />
                <button
                  type="submit"
                  disabled={submitting}
                  style={{
                    background: submitting ? '#90caf9' : '#1976d2',
                    color: 'white',
                    border: 'none',
                    borderRadius: 6,
                    padding: '12px',
                    fontWeight: 600,
                    fontSize: '1rem',
                    cursor: submitting ? 'not-allowed' : 'pointer',
                    marginTop: 8,
                    transition: 'background 0.2s',
                  }}
                >
                  {submitting ? 'Sending...' : 'Send Message'}
                </button>
                {result && (
                  <Typography color={result.success ? 'success.main' : 'error'} sx={{ mt: 1 }}>
                    {result.message}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default Contact;
